import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '@/lib/axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Plus, BookOpen, LogOut, ArrowRight, X, Megaphone } from 'lucide-react';
import AppShell from '@/components/AppShell';
import CreateBookModal from '@/components/CreateBookModal';
import BookCard from '@/components/BookCard';
import PendingInvitations from '@/components/PendingInvitations';
import { StoryEmptyState, StoryPageHeader, StorySectionHeader, StorySurface } from '@/components/story/StoryPrimitives';


function DashboardPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [books, setBooks] = useState([]);
  const [sharedBooks, setSharedBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dismissed_announcements') || '[]'); } catch { return []; }
  });

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    const [userResult, booksResult, sharedResult, messagesResult] = await Promise.allSettled([
      axios.get(`${API}/auth/me`),
      axios.get(`${API}/books`),
      axios.get(`${API}/shared-books`),
      axios.get(`${API}/messages`),
    ]);

    if (userResult.status === 'rejected') {
      window.location.href = '/login';
      return;
    }
    setUser(userResult.value.data);

    if (booksResult.status === 'rejected') {
      toast.error('Could not load your books. Please refresh the page.');
    } else {
      setBooks(booksResult.value.data);
    }

    if (sharedResult.status === 'rejected') {
      toast.error('Could not load shared books. Some books may be missing.');
      setSharedBooks([]);
    } else {
      setSharedBooks(sharedResult.value.data);
    }
    if (messagesResult.status === 'fulfilled') {
      setAnnouncements(messagesResult.value.data?.announcements || []);
    }

    setLoading(false);
  };

  const dismissAnnouncement = (id) => {
    const next = [...dismissedIds, id];
    setDismissedIds(next);
    localStorage.setItem('dismissed_announcements', JSON.stringify(next));
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
  };

  const handleCreateBook = async (bookData) => {
    try {
      const response = await axios.post(`${API}/books`, bookData);
      const newBook = response.data;
      setBooks([...books, newBook]);
      setShowCreateModal(false);
      navigate(`/books/${newBook.book_id}`);
    } catch (error) {
      console.error('Failed to create book:', error);
    }
  };

  const handleDeleteBook = async (bookId) => {
    try {
      await axios.delete(`${API}/books/${bookId}`);
      setBooks(books.filter(b => b.book_id !== bookId));
    } catch (error) {
      console.error('Failed to delete book:', error);
    }
  };

  if (loading) {
    return (
      <div className="story-page flex items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-[var(--story-primary)]/20 border-t-[var(--story-primary)]"></div>
      </div>
    );
  }

  const allBooks = [
    ...books.map(b => ({ ...b, _owned: true })),
    ...sharedBooks.map(b => ({ ...b, _owned: false })),
  ];
  const recentBook = [...allBooks].sort((a, b) =>
    new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0)
  )[0];

  const contextualItems = [
    { icon: Plus, label: 'New Book', onClick: () => setShowCreateModal(true), testId: 'start-new-book-button' },
    { divider: true },
    { icon: LogOut, label: 'Sign Out', onClick: handleLogout, variant: 'destructive', testId: 'logout-button' },
  ];

  return (
    <AppShell contextualItems={contextualItems} className="mobile-page overflow-x-hidden">
      <main className="story-container pt-14 pb-6 sm:pt-12 sm:pb-10">
        {announcements.filter(a => !dismissedIds.includes(a.announcement_id)).map(a => (
          <div key={a.announcement_id} className="mb-4 flex items-start gap-3 rounded-xl border border-[var(--story-line)] bg-[var(--story-surface)] px-4 py-3">
            <Megaphone className="mt-0.5 w-4 h-4 flex-shrink-0 text-[var(--story-primary)]" />
            <div className="flex-1 min-w-0">
              <p className="story-label">{a.title}</p>
              <p className="story-body text-sm mt-0.5">{a.message}</p>
            </div>
            <button onClick={() => dismissAnnouncement(a.announcement_id)} className="flex-shrink-0 text-[var(--story-muted)] hover:text-[var(--story-ink)]">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        <PendingInvitations onAccept={loadDashboard} />

        <StoryPageHeader
          eyebrow={user?.name ? `Welcome, ${user.name.split(' ')[0]}` : 'My books'}
          title={recentBook ? 'Pick up where you left off.' : 'Start your first story.'}
          description="Keep your books, interviews, drafts, and shared stories in one quiet workspace."
          actions={(
            <>
              {recentBook && (
                <Button
                  onClick={() => navigate(`/books/${recentBook.book_id}`)}
                  className="story-button-primary px-4 text-sm"
                >
                  <BookOpen className="w-3.5 h-3.5 mr-1.5" />
                  <span className="truncate max-w-[150px]">Continue</span>
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              )}
              <Button
                onClick={() => setShowCreateModal(true)}
                data-testid="start-new-book-button-mobile"
                variant="outline"
                className="story-button-secondary px-4 text-sm"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                New book
              </Button>
            </>
          )}
        />

        <StorySurface className="mb-5 p-3.5 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="story-meta">
              {allBooks.length.toLocaleString()} {allBooks.length === 1 ? 'book' : 'books'}
              {sharedBooks.length > 0 ? ` · ${sharedBooks.length} shared with you` : ''}
            </p>
            {recentBook && (
              <p className="story-meta truncate">
                Last opened: <span className="text-[var(--story-ink)]">{recentBook.title}</span>
              </p>
            )}
          </div>
        </StorySurface>

        <StorySectionHeader title="Your books" count={allBooks.length > 0 ? allBooks.length : undefined} />

        {allBooks.length === 0 ? (
          <StoryEmptyState
            icon={BookOpen}
            title={t('dashboard.noBooks')}
            description={t('dashboard.noBooksDesc')}
            action={(
              <Button
                onClick={() => setShowCreateModal(true)}
                data-testid="create-first-book-button"
                className="story-button-primary px-6"
              >
                <Plus className="w-4 h-4 mr-2" /> {t('dashboard.createFirst')}
              </Button>
            )}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
            {books.map((book) => (
              <BookCard
                key={book.book_id}
                book={book}
                onDelete={handleDeleteBook}
                onClick={() => navigate(`/books/${book.book_id}`)}
              />
            ))}
            {sharedBooks.map((book) => (
              <BookCard
                key={book.book_id}
                book={book}
                onClick={() => navigate(`/books/${book.book_id}`)}
              />
            ))}
          </div>
        )}
      </main>

      {showCreateModal && (
        <CreateBookModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateBook}
        />
      )}
    </AppShell>
  );
}

export default DashboardPage;
